import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { TimesheetDay } from '../../types/reports.types'

interface LatenessDayCellProps {
  day: TimesheetDay | undefined
  dateStr: string
  isToday: boolean
  isWeekend: boolean
}

const getCellColorClass = (day: TimesheetDay | undefined): string => {
  if (!day) return 'bg-gray-50'
  if (!day.is_working_day) return 'bg-gray-100 text-gray-400'
  if (!day.check_in && day.scheduled_start) return 'bg-red-50 text-red-600'
  if (!day.late_minutes || day.late_minutes <= 0) return ''
  if (day.late_minutes <= 15) return 'bg-yellow-100 text-yellow-700'
  if (day.late_minutes <= 30) return 'bg-orange-100 text-orange-700'
  return 'bg-red-100 text-red-700'
}

export function LatenessDayCell({
  day,
  dateStr,
  isToday,
  isWeekend,
}: LatenessDayCellProps) {
  const colorClass = getCellColorClass(day)

  let displayContent: React.ReactNode
  if (!day) {
    displayContent = <span className="text-[10px] text-gray-300">-</span>
  } else if (!day.is_working_day) {
    displayContent = <span className="text-[10px]">В</span>
  } else if (!day.check_in && day.scheduled_start) {
    displayContent = <span className="text-[10px] font-medium">Н</span>
  } else if (day.late_minutes > 0) {
    displayContent = (
      <span className="text-[10px] font-bold">{day.late_minutes}</span>
    )
  } else {
    displayContent = null
  }

  const tooltipContent = day ? (
    <div className="text-xs space-y-1">
      <div className="font-medium">{dateStr}</div>
      {day.is_working_day ? (
        <>
          <div>
            График: {day.scheduled_start || '?'} - {day.scheduled_end || '?'}
          </div>
          {day.check_in && (
            <div>
              Приход:{' '}
              <span className={day.is_late ? 'text-orange-500 font-medium' : ''}>
                {day.check_in}
              </span>
              {day.is_late && day.late_minutes > 0 && (
                <span className="text-orange-500"> (+{day.late_minutes} мин)</span>
              )}
            </div>
          )}
          {!day.check_in && <div className="text-red-500">Нет отметки о приходе</div>}
        </>
      ) : (
        <div className="text-gray-500">Выходной</div>
      )}
    </div>
  ) : (
    <div className="text-xs text-gray-500">Нет данных</div>
  )

  return (
    <td
      className={cn(
        'p-0 border-l',
        isToday && 'ring-2 ring-blue-400 ring-inset',
      )}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'h-8 w-full flex items-center justify-center',
                colorClass,
                isWeekend && day?.is_working_day && 'border-l-2 border-l-orange-400',
              )}
            >
              {displayContent}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[250px]">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </td>
  )
}
